import React from 'react';
import { MONEY_DECIMALS } from '../../../../../utils/money';
import {
  computeCustomRemainder,
  computeEqualSplitAmount,
  computeEqualSplitAmounts,
  isCustomSplitValid,
} from './splitPayment.utils';

export type SplitDialogMode = 'root' | 'equal' | 'custom';

export type UseSplitPaymentDialogStateArgs = {
  isOpen: boolean;
  roundAmounts?: boolean;
  totalAmount: number;
  /** with-tip grand total split across all tabs (equal + custom + summary); defaults to totalAmount */
  equalSplitTotal?: number;
};

export type SplitPaymentDialogState = {
  mode: SplitDialogMode;
  equalSplitCount: number | null;
  customSplitInput: string;
  equalSplitAmount: number;
  equalSplitAmounts: number[];
  customSplitAmount: number;
  customSplitRemainder: number;
  customValid: boolean;
  setMode: (mode: SplitDialogMode) => void;
  setEqualSplitCount: (count: number | null) => void;
  handleCustomSplitInputChange: (value: string) => void;
  resetToRoot: () => void;
};

export function useSplitPaymentDialogState(
  args: UseSplitPaymentDialogStateArgs,
): SplitPaymentDialogState {
  const { isOpen, totalAmount, equalSplitTotal = totalAmount, roundAmounts = MONEY_DECIMALS === 0 } = args;
  const [mode, setMode] = React.useState<SplitDialogMode>('root');
  const [equalSplitCount, setEqualSplitCount] = React.useState<number | null>(null);
  const [customSplitInput, setCustomSplitInput] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setMode('root');
      setEqualSplitCount(null);
      setCustomSplitInput('');
    }
  }, [isOpen]);

  const equalSplitAmount = equalSplitCount ? computeEqualSplitAmount(equalSplitTotal, equalSplitCount, roundAmounts) : 0;
  const equalSplitAmounts = equalSplitCount ? computeEqualSplitAmounts(equalSplitTotal, equalSplitCount, roundAmounts) : [];
  const customSplitAmount = Number(customSplitInput || '0');
  const customSplitRemainder = computeCustomRemainder(equalSplitTotal, customSplitAmount, roundAmounts);
  const customValid = isCustomSplitValid(equalSplitTotal, customSplitAmount, roundAmounts);

  const handleCustomSplitInputChange = (value: string) => {
    if ((roundAmounts ? /^\d*$/ : /^\d*(\.\d?)?$/).test(value)) {
      setCustomSplitInput(value);
    }
  };

  const resetToRoot = () => {
    setMode('root');
  };

  return {
    mode,
    equalSplitCount,
    customSplitInput,
    equalSplitAmount,
    equalSplitAmounts,
    customSplitAmount,
    customSplitRemainder,
    customValid,
    setMode,
    setEqualSplitCount,
    handleCustomSplitInputChange,
    resetToRoot,
  };
}
